'use client';
import { useState, useEffect, useCallback } from 'react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import { Bell, Mail, ChevronDown, Menu, X, LogOut, User, Zap, Bookmark, PieChart, Trophy, Settings, Swords, Radio, Gift, Users, MessageCircle, MessagesSquare, Globe, Newspaper, Home, Star, GraduationCap, BookOpenCheck } from 'lucide-react';
import AppLogo from '@/components/ui/AppLogo';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase/client';
import { useLang } from '@/lib/i18n/LanguageContext';

// Persistent top-bar nav — visible on EVERY page (unlike FeedLeftSidebar, which only
// renders on the home feed). Without this, leaving the feed left no way back except the
// browser's back button or clicking the logo (owner-reported gap, 2026-09-01).
// Trimmed to the 4-item set from the design_handoff_layout_unifikacija handoff
// (2026-09-01) — identical on every page, only the active link changes. The rest
// (Channels, Watchlist, Battles, …) stay reachable via the profile dropdown / mobile menu.
// "Signals" links to /home-feed, not '/' — for a logged-out visitor '/' renders the
// marketing LandingPage (see src/app/page.tsx), so linking straight to '/' from this
// nav would just loop back to the landing page instead of showing actual signals.
// /home-feed always renders the real feed regardless of auth state.
const TOP_NAV_LINKS = [
  { href: '/home-feed', label: 'Signals', icon: Home },
  { href: '/forum', label: 'Forum', icon: MessagesSquare },
  { href: '/live', label: 'Live', icon: Radio },
  { href: '/leaderboard', label: 'Traders', icon: Trophy },
  { href: '/premium', label: 'Premium', icon: Star },
];
// Search + market filtering live on the feed itself (FeedFilterBar) — the header carries only
// navigation, language and account actions, identical on every page.
interface NavbarProps {
  onPostTrade?: () => void;
}
export default function Navbar({ onPostTrade }: NavbarProps) {
  const pathname = usePathname();
  const router = useRouter();
  const { user, loading: authLoading, signOut, profile } = useAuth();
  const { lang, setLang, t } = useLang();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [dmUnread, setDmUnread] = useState(0);
  // DM unread badge — single dm_unread_count() RPC, refreshed on route change + realtime.
  const loadDmUnread = useCallback(async () => {
    if (!user) { setDmUnread(0); return; }
    try {
      const { data } = await supabase.rpc('dm_unread_count');
      setDmUnread(typeof data === 'number' ? data : 0);
    } catch { /* ignore */ }
  }, [user]);
  // Fetch unread notification count
  const loadUnread = useCallback(async () => {
    if (!user) return;
    try {
      const res = await fetch(`/api/notifications?userId=${user.id}&unreadOnly=true`);
      if (res.ok) {
        const data = await res.json();
        setUnreadCount(data.unreadCount ?? 0);
      }
    } catch { /* ignore */ }
  }, [user]);

  // Refresh the unread count on route change + when the tab regains focus.
  // This guarantees the badge clears after reading on /notifications even if the
  // realtime UPDATE event doesn't fire.
  useEffect(() => {
    loadUnread();
  }, [pathname, loadUnread]);

  useEffect(() => {
    const onFocus = () => loadUnread();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [loadUnread]);

  // The /notifications page marks everything read but holds its own state; it emits
  // smt:notifications-read so the bell badge clears instantly even while we stay on the page.
  useEffect(() => {
    const onRead = () => setUnreadCount(0);
    window.addEventListener('smt:notifications-read', onRead);
    return () => window.removeEventListener('smt:notifications-read', onRead);
  }, []);

  useEffect(() => { loadDmUnread(); }, [pathname, loadDmUnread]);
  useEffect(() => {
    if (!user) { setDmUnread(0); return; }
    const dmCh = supabase
      .channel(`navbar-dm-${user.id}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'LiveChatMessage' }, (payload) => {
        // Room-less rows (global + symbol live rooms) can never change dm_unread_count —
        // skip the RPC or every symbol-room message fans out one call per online user.
        if ((payload.new as { roomId: string | null }).roomId != null) loadDmUnread();
      })
      .subscribe();
    return () => { supabase.removeChannel(dmCh); };
  }, [user, loadDmUnread]);

  // Initial fetch + realtime subscription for live notification updates
  useEffect(() => {
    if (!user) {
      setUnreadCount(0);
      return;
    }
    loadUnread();

    // Subscribe to new notifications for this user via Supabase Realtime
    const channel = supabase
      .channel(`navbar-notif-${user.id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'Notification',
          filter: `userId=eq.${user.id}`,
        },
        () => {
          // New notification arrived — bump the count
          setUnreadCount((prev) => prev + 1);
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'Notification',
          filter: `userId=eq.${user.id}`,
        },
        () => {
          // Notification marked as read — refetch accurate count
          loadUnread();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, loadUnread]);
  async function handleSignOut() {
    try {
      await signOut();
    } catch { /* ignore */ }
    setProfileOpen(false);
    router.push('/sign-up-login-screen');
  }
  const isAuthPage = pathname?.includes('sign-up-login');
  if (isAuthPage) return null;
  const isLoggedIn = !!user;
  // Derive display values from context profile or fall back to auth user email
  const displayUsername = profile?.username || user?.email?.split('@')[0] || 'trader';
  const displayName = profile?.displayName || profile?.username || user?.email?.split('@')[0] || 'Trader';
  const avatarUrl = profile?.avatarUrl || null;
  const avatarInitial = (displayName).charAt(0).toUpperCase();
  return (
    <nav
      className="sticky top-0 z-50 w-full"
      style={{
        background: 'rgba(255, 255, 255, 0.92)',
        backdropFilter: 'blur(12px)',
        borderBottom: '1px solid var(--border)',
      }}
    >
      <div style={{ maxWidth: 1280, margin: '0 auto', padding: '13px 24px' }}>
        <div className="flex items-center gap-4">
          {/* Logo */}
          <Link href="/" className="flex items-center gap-2 shrink-0">
            <AppLogo size={38} />
            <span className="font-heading font-extrabold text-lg hidden sm:block tracking-tight">
              <span style={{ color: 'var(--text-primary)' }}>ShowMe</span>
              <span style={{ color: 'var(--accent)' }}>Trade</span>
            </span>
          </Link>
          {/* Persistent nav — desktop, visible on every page, centered, identical everywhere */}
          <nav className="hidden lg:flex flex-1 items-center justify-center shrink-0" style={{ gap: 'clamp(16px, 3vw, 44px)' }}>
            {TOP_NAV_LINKS.map(({ href, label, icon: LinkIcon }) => {
              const isActive = href === '/home-feed' ? (pathname === '/' || pathname === '/home-feed') : !!pathname?.startsWith(href);
              return (
                <Link
                  key={href}
                  href={href}
                  className="flex items-center gap-1.5 whitespace-nowrap transition-colors duration-150"
                  style={{ fontSize: 15.5, fontWeight: isActive ? 700 : 600, color: isActive ? 'var(--accent-hover)' : 'var(--text-body)' }}
                  onMouseEnter={(e) => { if (!isActive) (e.currentTarget as HTMLAnchorElement).style.color = 'var(--text-primary)'; }}
                  onMouseLeave={(e) => { if (!isActive) (e.currentTarget as HTMLAnchorElement).style.color = 'var(--text-body)'; }}
                >
                  <LinkIcon size={15} />
                  {t(label)}
                </Link>
              );
            })}
          </nav>
          {/* Right actions — ml-auto pins this flush to the right edge on mobile, where the
              centered nav above is hidden (display:none) and its flex-1 can't do the pushing. */}
          <div className="flex items-center gap-2 ml-auto">
            {/* Language switcher — SR|EN pill, SR is the default, EN on demand */}
            <div
              className="hidden sm:inline-flex items-center p-[3px] rounded-full font-mono-nums"
              style={{ background: 'var(--bg-sunken)' }}
            >
              {(['sr', 'en'] as const).map((l) => (
                <button
                  key={l}
                  onClick={() => setLang(l)}
                  className="px-3 py-1 rounded-full text-xs font-bold transition-colors duration-150"
                  style={{
                    background: lang === l ? 'var(--text-primary)' : 'transparent',
                    color: lang === l ? 'var(--bg-base)' : 'var(--text-secondary)',
                  }}
                  title={l === 'sr' ? 'Prebaci na srpski' : 'Switch to English'}
                  aria-label={l === 'sr' ? 'Prebaci na srpski' : 'Switch to English'}
                >
                  {l.toUpperCase()}
                </button>
              ))}
            </div>
            {/* While auth is resolving, show a neutral placeholder to prevent flicker */}
            {authLoading ? (
              <div className="w-8 h-8 rounded-full animate-pulse" style={{ background: 'var(--bg-sunken)' }} />
            ) : isLoggedIn ? (
              <>
                <Link
                  href="/messages"
                  aria-label={t('Messages')}
                  className="relative p-2 rounded-lg transition-colors duration-150 inline-flex items-center justify-center"
                  style={{ color: 'var(--text-secondary)' }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.background = 'var(--bg-sunken)'; (e.currentTarget as HTMLAnchorElement).style.color = 'var(--text-primary)'; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.background = 'transparent'; (e.currentTarget as HTMLAnchorElement).style.color = 'var(--text-secondary)'; }}
                >
                  <Mail size={18} />
                  {dmUnread > 0 && (
                    <span
                      className="absolute top-1 right-1 min-w-[16px] h-4 rounded-full flex items-center justify-center font-bold px-0.5 font-mono-nums"
                      style={{ background: 'var(--bearish)', color: 'white', fontSize: '10px', lineHeight: 1 }}
                    >
                      {dmUnread > 9 ? '9+' : dmUnread}
                    </span>
                  )}
                </Link>
                <Link
                  href="/notifications"
                  className="relative p-2 rounded-lg transition-colors duration-150 inline-flex items-center justify-center"
                  style={{ color: 'var(--text-secondary)' }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.background = 'var(--bg-sunken)'; (e.currentTarget as HTMLAnchorElement).style.color = 'var(--text-primary)'; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.background = 'transparent'; (e.currentTarget as HTMLAnchorElement).style.color = 'var(--text-secondary)'; }}
                >
                  <Bell size={18} />
                  {unreadCount > 0 && (
                    <span
                      className="absolute top-1 right-1 min-w-[16px] h-4 rounded-full flex items-center justify-center text-xs font-bold px-0.5 font-mono-nums"
                      style={{ background: 'var(--bearish)', color: 'white', fontSize: '10px', lineHeight: 1 }}
                    >
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </Link>
                <button
                  onClick={onPostTrade}
                  className="hidden sm:flex btn-primary items-center gap-1.5 px-3 py-2 text-sm"
                >
                  <Zap size={14} />
                  {t('Post Idea')}
                </button>

                {/* ── Mobile: Avatar = direct link to profile ── */}
                <Link
                  href="/user-profile"
                  className="md:hidden shrink-0"
                >
                  {avatarUrl ? (
                    <img
                      src={avatarUrl}
                      alt={displayName}
                      className="w-8 h-8 rounded-full object-cover"
                      style={{ border: '2px solid var(--border)' }}
                    />
                  ) : (
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold"
                      style={{ background: 'var(--accent)', color: 'var(--bg-base)' }}
                    >
                      {avatarInitial}
                    </div>
                  )}
                </Link>

                {/* ── Desktop: Avatar button with profile dropdown ── */}
                <div className="relative hidden md:block">
                  <button
                    onClick={() => setProfileOpen(!profileOpen)}
                    className="flex items-center gap-2 px-2 py-1.5 rounded-lg transition-colors duration-150"
                    onMouseEnter={(e) => ((e.currentTarget as HTMLButtonElement).style.background = 'var(--bg-sunken)')}
                    onMouseLeave={(e) => ((e.currentTarget as HTMLButtonElement).style.background = 'transparent')}
                  >
                    {avatarUrl ? (
                      <img
                        src={avatarUrl}
                        alt={displayName}
                        className="w-8 h-8 rounded-full object-cover shrink-0"
                      />
                    ) : (
                      <div
                        className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold shrink-0"
                        style={{ background: 'var(--accent)', color: 'var(--bg-base)' }}
                      >
                        {avatarInitial}
                      </div>
                    )}
                    <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                      {displayUsername}
                    </span>
                    <ChevronDown size={14} style={{ color: 'var(--text-muted)' }} />
                  </button>
                  {profileOpen && (
                    <div
                      className="absolute top-full mt-1 right-0 w-48 rounded-xl overflow-hidden animate-fade-in z-50"
                      style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', boxShadow: '0 16px 40px rgba(28,43,38,0.12)' }}
                    >
                      <div className="px-4 py-3 border-b" style={{ borderColor: 'var(--border)' }}>
                        <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{displayName}</p>
                        <p className="text-xs" style={{ color: 'var(--text-muted)' }}>@{displayUsername}</p>
                      </div>
                      <Link
                        href={`/profile/${displayUsername}`}
                        onClick={() => setProfileOpen(false)}
                        className="flex items-center gap-3 px-4 py-2.5 text-sm transition-colors duration-100"
                        style={{ color: 'var(--text-secondary)' }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-sunken)'; e.currentTarget.style.color = 'var(--text-primary)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-secondary)'; }}
                      >
                        <User size={15} />
                        {t('My Profile')}
                      </Link>
                      <Link
                        href="/followers"
                        onClick={() => setProfileOpen(false)}
                        className="flex items-center gap-3 px-4 py-2.5 text-sm transition-colors duration-100"
                        style={{ color: 'var(--text-secondary)' }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-sunken)'; e.currentTarget.style.color = 'var(--text-primary)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-secondary)'; }}
                      >
                        <Users size={15} />
                        {t('Followers & Following')}
                      </Link>
                      <Link
                        href="/portfolio"
                        onClick={() => setProfileOpen(false)}
                        className="flex items-center gap-3 px-4 py-2.5 text-sm transition-colors duration-100"
                        style={{ color: 'var(--text-secondary)' }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-sunken)'; e.currentTarget.style.color = 'var(--text-primary)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-secondary)'; }}
                      >
                        <PieChart size={15} />
                        {t('Portfolio')}
                      </Link>
                      <Link
                        href="/leaderboard"
                        onClick={() => setProfileOpen(false)}
                        className="flex items-center gap-3 px-4 py-2.5 text-sm transition-colors duration-100"
                        style={{ color: 'var(--text-secondary)' }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-sunken)'; e.currentTarget.style.color = 'var(--text-primary)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-secondary)'; }}
                      >
                        <Trophy size={15} />
                        {t('Leaderboard')}
                      </Link>
                      <Link
                        href="/battles"
                        onClick={() => setProfileOpen(false)}
                        className="flex items-center gap-3 px-4 py-2.5 text-sm transition-colors duration-100"
                        style={{ color: 'var(--text-secondary)' }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-sunken)'; e.currentTarget.style.color = 'var(--text-primary)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-secondary)'; }}
                      >
                        <Swords size={15} />
                        {t('Trade Battles')}
                      </Link>
                      <Link
                        href="/watchlist"
                        onClick={() => setProfileOpen(false)}
                        className="flex items-center gap-3 px-4 py-2.5 text-sm transition-colors duration-100"
                        style={{ color: 'var(--text-secondary)' }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-sunken)'; e.currentTarget.style.color = 'var(--text-primary)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-secondary)'; }}
                      >
                        <Bookmark size={15} />
                        {t('Watchlist')}
                      </Link>
                      <Link
                        href="/channels"
                        onClick={() => setProfileOpen(false)}
                        className="flex items-center gap-3 px-4 py-2.5 text-sm transition-colors duration-100"
                        style={{ color: 'var(--text-secondary)' }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-sunken)'; e.currentTarget.style.color = 'var(--text-primary)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-secondary)'; }}
                      >
                        <Radio size={15} />
                        {t('Trader Channels')}
                      </Link>
                      <Link
                        href="/forum"
                        onClick={() => setProfileOpen(false)}
                        className="flex items-center gap-3 px-4 py-2.5 text-sm transition-colors duration-100"
                        style={{ color: 'var(--text-secondary)' }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-sunken)'; e.currentTarget.style.color = 'var(--text-primary)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-secondary)'; }}
                      >
                        <MessagesSquare size={15} />
                        {t('Forum')}
                      </Link>
                      <Link
                        href="/live"
                        onClick={() => setProfileOpen(false)}
                        className="flex items-center gap-3 px-4 py-2.5 text-sm transition-colors duration-100"
                        style={{ color: 'var(--text-secondary)' }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-sunken)'; e.currentTarget.style.color = 'var(--text-primary)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-secondary)'; }}
                      >
                        <MessageCircle size={15} />
                        {t('Live Chat')}
                      </Link>
                      <Link
                        href="/akademija"
                        onClick={() => setProfileOpen(false)}
                        className="flex items-center gap-3 px-4 py-2.5 text-sm transition-colors duration-100"
                        style={{ color: 'var(--text-secondary)' }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-sunken)'; e.currentTarget.style.color = 'var(--text-primary)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-secondary)'; }}
                      >
                        <GraduationCap size={15} />
                        {t('Academy')}
                      </Link>
                      <Link
                        href="/studije"
                        onClick={() => setProfileOpen(false)}
                        className="flex items-center gap-3 px-4 py-2.5 text-sm transition-colors duration-100"
                        style={{ color: 'var(--text-secondary)' }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-sunken)'; e.currentTarget.style.color = 'var(--text-primary)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-secondary)'; }}
                      >
                        <BookOpenCheck size={15} />
                        {t('Trade studies')}
                      </Link>
                      <Link
                        href="/referral"
                        onClick={() => setProfileOpen(false)}
                        className="flex items-center gap-3 px-4 py-2.5 text-sm transition-colors duration-100"
                        style={{ color: 'var(--text-secondary)' }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-sunken)'; e.currentTarget.style.color = 'var(--text-primary)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-secondary)'; }}
                      >
                        <Gift size={15} />
                        {t('Invite & Earn')}
                      </Link>
                      <Link
                        href="/user-profile?tab=settings"
                        onClick={() => setProfileOpen(false)}
                        className="flex items-center gap-3 px-4 py-2.5 text-sm transition-colors duration-100"
                        style={{ color: 'var(--text-secondary)' }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-sunken)'; e.currentTarget.style.color = 'var(--text-primary)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-secondary)'; }}
                      >
                        <Settings size={15} />
                        {t('Settings')}
                      </Link>
                      <div className="border-t my-1" style={{ borderColor: 'var(--border)' }} />
                      <button
                        onClick={handleSignOut}
                        className="flex items-center gap-3 w-full px-4 py-2.5 text-sm transition-colors duration-100"
                        style={{ color: 'var(--bearish)' }}
                        onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--bearish-muted)'; }}
                        onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
                      >
                        <LogOut size={15} />
                        {t('Sign Out')}
                      </button>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="flex items-center gap-2">
                <Link href="/sign-up-login-screen" className="btn-ghost px-3 sm:px-4 py-2 text-sm font-medium whitespace-nowrap">
                  {t('Sign In')}
                </Link>
                <Link href="/sign-up-login-screen" className="btn-primary px-3 sm:px-4 py-2 text-sm whitespace-nowrap">
                  {t('Sign Up')}
                </Link>
              </div>
            )}
            <button
              className="md:hidden p-2 rounded-lg"
              onClick={() => setMobileOpen(!mobileOpen)}
              style={{ color: 'var(--text-secondary)' }}
            >
              {mobileOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
        {/* Mobile menu */}
        {mobileOpen && (
          <div className="md:hidden py-4 border-t animate-fade-in" style={{ borderColor: 'var(--border)' }}>
            <div className="flex flex-col gap-3 px-2">
              {/* Main menu — the same 4 links as the desktop header, for EVERY visitor
                  (owner-reported 2026-09-02: logged-out phones saw only language + market chips) */}
              <nav className="grid grid-cols-2 gap-1.5">
                {TOP_NAV_LINKS.map(({ href, label, icon: LinkIcon }) => {
                  const isActive = href === '/home-feed' ? (pathname === '/' || pathname === '/home-feed') : !!pathname?.startsWith(href);
                  return (
                    <Link
                      key={href}
                      href={href}
                      onClick={() => setMobileOpen(false)}
                      className="flex items-center gap-2.5 px-3 py-3 rounded-xl text-sm font-semibold transition-colors"
                      style={{
                        background: isActive ? 'var(--accent-muted)' : 'var(--bg-sunken)',
                        color: isActive ? 'var(--accent)' : 'var(--text-primary)',
                        border: isActive ? '1px solid var(--accent)' : '1px solid var(--border)',
                      }}
                    >
                      <LinkIcon size={16} style={{ color: 'var(--accent)' }} />
                      {t(label)}
                    </Link>
                  );
                })}
              </nav>

              {/* (The SHOW · ME · TRADE+ strip that used to sit here was removed 2026-09-08 —
                  the floating bottom nav (MobileBottomNav) now owns those three, so the
                  hamburger showed them twice on the same screen.) */}

              {/* Language switcher — mobile */}
              <div className="flex gap-1.5">
                {(['sr', 'en'] as const).map((l) => (
                  <button
                    key={l}
                    onClick={() => setLang(l)}
                    className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition-colors duration-100"
                    style={{
                      background: lang === l ? 'var(--accent-muted)' : 'var(--bg-sunken)',
                      color: lang === l ? 'var(--accent)' : 'var(--text-secondary)',
                      border: lang === l ? '1px solid var(--accent)' : '1px solid var(--border)',
                    }}
                  >
                    <Globe size={13} />
                    {l === 'sr' ? 'Srpski' : 'English'}
                  </button>
                ))}
              </div>

              {/* All navigation links — logged in */}
              {isLoggedIn && (
                <>
                  <div className="grid grid-cols-2 gap-1.5 pt-1">
                    {[
                      { href: '/user-profile', icon: User, label: 'My Profile' },
                      { href: '/followers', icon: Users, label: 'Followers' },
                      { href: '/leaderboard', icon: Trophy, label: 'Leaderboard' },
                      { href: '/forum', icon: MessagesSquare, label: 'Forum' },
                      { href: '/vesti', icon: Newspaper, label: 'Vesti' },
                      { href: '/akademija', icon: GraduationCap, label: 'Academy' },
                      { href: '/studije', icon: BookOpenCheck, label: 'Trade studies' },
                      { href: '/channels', icon: Radio, label: 'Channels' },
                      { href: '/battles', icon: Swords, label: 'Battles' },
                      { href: '/portfolio', icon: PieChart, label: 'Portfolio' },
                      { href: '/watchlist', icon: Bookmark, label: 'Watchlist' },
                      { href: '/referral', icon: Gift, label: 'Invite & Earn' },
                      { href: '/user-profile?tab=settings', icon: Settings, label: 'Settings' },
                    ].map((item) => {
                      const Icon = item.icon;
                      const isActive = pathname?.includes(item.href);
                      return (
                        <Link
                          key={item.href}
                          href={item.href}
                          onClick={() => setMobileOpen(false)}
                          className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm transition-colors"
                          style={{
                            background: isActive ? 'var(--accent-muted)' : 'var(--bg-sunken)',
                            color: isActive ? 'var(--accent)' : 'var(--text-secondary)',
                            border: isActive ? '1px solid var(--accent)' : '1px solid transparent',
                          }}
                        >
                          <Icon size={15} />
                          {t(item.label)}
                        </Link>
                      );
                    })}
                  </div>
                  <button
                    onClick={() => { setMobileOpen(false); handleSignOut(); }}
                    className="flex items-center gap-2.5 w-full px-3 py-2.5 rounded-lg text-sm transition-colors"
                    style={{ background: 'var(--bearish-muted)', color: 'var(--bearish)', border: '1px solid var(--bearish)' }}
                  >
                    <LogOut size={15} />
                    {t('Sign Out')}
                  </button>
                </>
              )}

              {!isLoggedIn && !authLoading && (
                <div className="flex gap-2 pt-2">
                  <Link href="/sign-up-login-screen" className="flex-1 btn-ghost py-2.5 text-sm text-center font-medium">{t('Sign In')}</Link>
                  <Link href="/sign-up-login-screen" className="flex-1 btn-primary py-2.5 text-sm text-center">{t('Sign Up')}</Link>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      {profileOpen && (
        <div className="fixed inset-0 z-40" onClick={() => setProfileOpen(false)} />
      )}
    </nav>
  );
}
